import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { http } from '@/services/http';

// ─────────────────────────────────────────────────────────────────────────────
// Contexto de Evento Ativo (padrão Web Summit): identidade global, mas todo o
// social (Tap/networking/matchmaking) é scoped ao evento em que ESTOU agora.
// A lista vem de /users/me/active-events (só eventos com bilhete válido a
// decorrer). O activeEventId exposto é SEMPRE validado contra essa lista — nunca
// um id persistido "solto" — e o backend revalida (requireEventScope). Dupla rede.
// ─────────────────────────────────────────────────────────────────────────────

const activeEventSchema = z.object({
  id: z.string(),
  slug: z.string().nullish(),
  title: z.string().nullish(),
  venue_name: z.string().nullish(),
  venue_city: z.string().nullish(),
  starts_at: z.string().nullish(),
  ends_at: z.string().nullish(),
  status: z.string().nullish(),
  cover_url: z.string().nullish(),
  checked_in: z.boolean().nullish(),
});
export type ActiveEvent = z.infer<typeof activeEventSchema>;

async function getActiveEvents(): Promise<ActiveEvent[]> {
  try {
    const raw = await http.get<unknown>('/users/me/active-events');
    const arr = Array.isArray(raw) ? raw : ((raw as { data?: unknown })?.data ?? []);
    return z.array(activeEventSchema).parse(arr);
  } catch {
    return []; // convidado / 401 / offline → sem eventos elegíveis
  }
}

const STORAGE_KEY = 'mx.activeEventId';

interface EventCtx {
  events: ActiveEvent[];
  activeEvent: ActiveEvent | null;
  activeEventId: string | null;
  setActiveEventId: (id: string | null) => void;
  loading: boolean;
}
const Ctx = createContext<EventCtx | null>(null);

export function EventProvider({ children }: { children: ReactNode }) {
  const { data: events = [], isLoading } = useQuery({
    queryKey: ['active-events'], queryFn: getActiveEvents, staleTime: 60_000,
  });
  const [persistedId, setPersistedId] = useState<string | null>(() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  });

  const setActiveEventId = (id: string | null) => {
    setPersistedId(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* storage indisponível — degrada em memória */ }
  };

  // Resolução: se o persistido continua elegível, mantém. Senão default = 1.º da
  // lista (já ordenada no servidor: checked_in → live → data). Senão null.
  useEffect(() => {
    if (isLoading) return;
    const stillValid = persistedId && events.some((e) => e.id === persistedId);
    if (stillValid) return;
    const fallback = events[0]?.id ?? null;
    if (fallback !== persistedId) setActiveEventId(fallback);
  }, [events, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // activeEvent = só se o id casar com um evento elegível → nunca expõe id "solto".
  const activeEvent = useMemo(
    () => events.find((e) => e.id === persistedId) ?? null,
    [events, persistedId],
  );

  const value = useMemo<EventCtx>(() => ({
    events,
    activeEvent,
    activeEventId: activeEvent?.id ?? null,
    setActiveEventId,
    loading: isLoading,
  }), [events, activeEvent, isLoading]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useActiveEvent(): EventCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useActiveEvent() tem de ser usado dentro de <EventProvider>');
  return ctx;
}
